import { useAuthTokens } from '@/utils/tokenHelper';
import {
  useOverViewStore,
  useQuickPeekStore,
  useOverviewImagesStore,
} from '@/store/overViewStore'; // Import the images store
import { useOptionStore } from '@/store/optionStore';
import axios from 'axios';
import { useOverviewActivityStore } from '@/store/overviewActivityStore';
import { useCallback, useRef } from 'react';

const useFetchLocationDetails = () => {
  const { accessToken, refreshToken } = useAuthTokens();
  const { sessionId } = useOptionStore();
  const {
    addOverview,
    overview,
    addFestival,
    addPlaceOfInterest,
    clearOverViewData,
  } = useOverViewStore();
  const {
    addInfoPointer,
    addTravelPlanning,
    addModeOfTransport,
    clearQuickPeekData,
    setLoadingActivitiesFacilities,
    setLoadingTravelPlanningOnly,
    setLoadingNavigationAccessibility,
    setLoadingCulturalSocial,
    setLoadingHistoricalSouvenirs,
  } = useQuickPeekStore();
  const { setImages, clearImages, setActiveImage } = useOverviewImagesStore(); // Use the images store
  const { setOverviewActivity, setOverviewShow } = useOverviewActivityStore();
  const fetchingRef = useRef<string | null>(null);

  const fetchLocationDetails = useCallback(
    async location => {
      // Prevent duplicate calls for the same location
      if (!location?.name || fetchingRef.current === location.name) {
        return;
      }

      // Check if we already have data for this location
      if (overview.name === location.name) {
        return;
      }

      fetchingRef.current = location.name;
      setOverviewShow(true);

      // Clear existing data
      clearOverViewData();
      clearQuickPeekData();
      clearImages(); // Clear existing images
      setOverviewActivity(true);
      setActiveImage(false);
      const requestBody = {
        // sessionId: sessionId || "default-session-id",
        name: location.name,
        district: null,
        city: null,
        state:
          location.state && location.state.trim() !== ''
            ? location.state
            : null,
      };

      // Generate session_id if not available
      const currentSessionId =
        sessionId ||
        `session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

      const requestBodyQuickPeek = {
        user_id: 'string',
        session_id: currentSessionId,
        location: location.name,
      };

      try {
        // Call all 5 QuickPeek APIs in parallel
        // Using the new travel-assistant endpoints (same as chat screen)
        // All APIs will display in UI as soon as they complete, even if others fail
        console.log(
          '🔴 [useFetchLocationDetails] 🚀 CALLING 5 QuickPeek APIs in parallel:',
          {
            location: location.name,
            requestId: currentSessionId,
          }
        );

        // 1. Travel Planning APIs (parallel - travel-planning-only and navigation-accessibility)
        console.log(
          '🔴 [useFetchLocationDetails] 📞 Step 1: Calling Travel Planning APIs in parallel...'
        );
        setLoadingTravelPlanningOnly(true);
        setLoadingNavigationAccessibility(true);

        // Call all 5 APIs in parallel using Promise.allSettled
        // This ensures one API failure doesn't prevent others from succeeding
        // Each API will display in UI as soon as it completes
        const [
          travelPlanningOnlyResult,
          navigationAccessibilityResult,
          culturalSocialResult,
          historicalSouvenirsResult,
          activitiesFacilitiesResult,
        ] = await Promise.allSettled([
          axios.post(
            '/api/travel-assistant/quick-peek/travel-planning-only',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/navigation-accessibility',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/cultural-social',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/historical-souvenirs',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/activities-facilities',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/cultural-social',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/historical-souvenirs',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
          axios.post(
            '/api/travel-assistant/quick-peek/activities-facilities',
            {
              user_id: requestBodyQuickPeek.user_id,
              session_id: requestBodyQuickPeek.session_id,
              location: requestBodyQuickPeek.location,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ),
        ]);

        // Process all 5 responses in order (display each as soon as it completes)
        // 1. Process travel-planning-only response first (display in order)
        // Handle success or failure independently
        if (travelPlanningOnlyResult.status === 'fulfilled') {
          console.log(
            '🔴 [useFetchLocationDetails] ✅ Travel Planning Only API response received'
          );
          const travelPlanningOnlyData =
            travelPlanningOnlyResult.value.data?.data?.response ||
            travelPlanningOnlyResult.value.data?.response;
          if (travelPlanningOnlyData) {
            if (travelPlanningOnlyData.infoPointers) {
              travelPlanningOnlyData.infoPointers.forEach(addInfoPointer);
            }
            if (travelPlanningOnlyData.modeOfTransport) {
              travelPlanningOnlyData.modeOfTransport.forEach(
                addModeOfTransport
              );
            }
            if (travelPlanningOnlyData.travelPlanning) {
              addTravelPlanning(travelPlanningOnlyData.travelPlanning);
            }
          }
        } else {
          console.error(
            '🔴 [useFetchLocationDetails] ❌ Travel Planning Only API failed:',
            travelPlanningOnlyResult.reason
          );
        }
        setLoadingTravelPlanningOnly(false);

        // Process navigation-accessibility response second (display in order)
        // Handle success or failure independently
        if (navigationAccessibilityResult.status === 'fulfilled') {
          console.log(
            '🔴 [useFetchLocationDetails] ✅ Navigation Accessibility API response received'
          );
          const navigationAccessibilityData =
            navigationAccessibilityResult.value.data?.data?.response ||
            navigationAccessibilityResult.value.data?.response;
          if (navigationAccessibilityData) {
            // Navigation/accessibility data will be merged into the store
            // The store will handle the merging automatically
          }
        } else {
          console.error(
            '🔴 [useFetchLocationDetails] ❌ Navigation Accessibility API failed:',
            navigationAccessibilityResult.reason
          );
        }
        setLoadingNavigationAccessibility(false);

        // 3. Process cultural-social response (display in order)
        // Handle success or failure independently
        if (culturalSocialResult.status === 'fulfilled') {
          console.log(
            '🔴 [useFetchLocationDetails] ✅ Cultural Social API response received'
          );
          const culturalSocialData =
            culturalSocialResult.value.data?.data?.response ||
            culturalSocialResult.value.data?.response;
          if (culturalSocialData) {
            // Cultural social data will be merged into the store
            // The store will handle the merging automatically
          }
        } else {
          console.error(
            '🔴 [useFetchLocationDetails] ❌ Cultural Social API failed:',
            culturalSocialResult.reason
          );
        }
        setLoadingCulturalSocial(false);

        // Process historical-souvenirs response second (display in order)
        // Handle success or failure independently
        if (historicalSouvenirsResult.status === 'fulfilled') {
          console.log(
            '🔴 [useFetchLocationDetails] ✅ Historical Souvenirs API response received'
          );
          const historicalSouvenirsData =
            historicalSouvenirsResult.value.data?.data?.response ||
            historicalSouvenirsResult.value.data?.response;
          if (historicalSouvenirsData) {
            // Historical souvenirs data will be merged into the store
            // The store will handle the merging automatically
          }
        } else {
          console.error(
            '🔴 [useFetchLocationDetails] ❌ Historical Souvenirs API failed:',
            historicalSouvenirsResult.reason
          );
        }
        setLoadingHistoricalSouvenirs(false);

        // 5. Process activities-facilities response (display in order)
        if (activitiesFacilitiesResult.status === 'fulfilled') {
          console.log(
            '🔴 [useFetchLocationDetails] ✅ Activities & Facilities API response received'
          );
          const activitiesFacilitiesData =
            activitiesFacilitiesResult.value.data?.data?.response ||
            activitiesFacilitiesResult.value.data?.response;
          if (activitiesFacilitiesData) {
            // Activities & facilities data will be merged into the store
            // The store will handle the merging automatically
          }
        } else {
          console.error(
            '🔴 [useFetchLocationDetails] ❌ Activities & Facilities API failed:',
            activitiesFacilitiesResult.reason
          );
        }
        setLoadingActivitiesFacilities(false);

        console.log(
          '🔴 [useFetchLocationDetails] ✅ All 5 QuickPeek APIs completed in parallel'
        );

        // Overview and Images APIs (run in parallel with QuickPeek)
        const overviewPromise = axios.post(
          '/api/result/overview',
          requestBody,
          {
            headers: {
              'x-access-token': accessToken,
              'x-refresh-token': refreshToken,
              'Content-Type': 'application/json',
            },
          }
        );

        const imagesPromise = axios.post(
          '/api/result/images-by-location',
          requestBody,
          {
            headers: {
              'x-access-token': accessToken,
              'x-refresh-token': refreshToken,
              'Content-Type': 'application/json',
            },
          }
        );

        overviewPromise
          .then(response => {
            const overviewData = response.data.data.response;

            // Save overview data
            if (overviewData?.overview) {
              addOverview({
                name: overviewData.overview.name,
                description: overviewData.overview.description,
                district: overviewData.overview.district,
                city: overviewData.overview.city,
                rating: overviewData.overview.rating,
                reviews: overviewData.overview.reviews,
                type: overviewData.overview.type,
                state: overviewData.overview.state,
                pincode: overviewData.overview.pincode,
                pointers: overviewData.overview.pointers || [],
              });
            }

            // ✅ Add festivals and places of interest here
            overviewData.festivals?.forEach(addFestival);
            overviewData.placesOfInterest?.forEach(addPlaceOfInterest);
          })
          .catch(err => console.error('Overview API Error:', err));

        imagesPromise
          .then(response => {
            const imagesData = response.data.data;

            if (imagesData?.length > 0) {
              setImages(imagesData);
            } else {
              setActiveImage(true);
            }
          })
          .catch(err => console.error('Images API Error:', err));

        // Wait for remaining promises to settle before marking activity as complete
        await Promise.allSettled([overviewPromise, imagesPromise]);
        setOverviewActivity(false);
        fetchingRef.current = null;
      } catch (err) {
        setOverviewShow(false);
        fetchingRef.current = null;
        console.error('Error:', err);
      }
    },
    [
      accessToken,
      refreshToken,
      sessionId,
      overview.name,
      addOverview,
      addFestival,
      addPlaceOfInterest,
      clearOverViewData,
      addInfoPointer,
      addTravelPlanning,
      addModeOfTransport,
      clearQuickPeekData,
      setLoadingActivitiesFacilities,
      setLoadingTravelPlanningOnly,
      setLoadingNavigationAccessibility,
      setLoadingCulturalSocial,
      setLoadingHistoricalSouvenirs,
      setImages,
      clearImages,
      setActiveImage,
      setOverviewActivity,
      setOverviewShow,
    ]
  );

  return { fetchLocationDetails };
};

export default useFetchLocationDetails;
